import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PageConfig from '@/component/layout/PageConfig.tsx'
import DashboardMap from '@/pages/dashboard/DashboardMapDefault.tsx'
import DashboardMapForCareManager from '@/pages/dashboard/DashboardMapForCareManager.tsx'
import UrgentNotification from '@/pages/dashboard/UrgentNotification.tsx'
import BioAlertDetailModal from '@/pages/notification/bio/BioAlertModal.tsx'
import useQueryGetUsersBbox, { IReqGetUsersBbox } from '@/api/user/getUsersBbox.ts'
import useQueryGetMe, { DashboardType } from '@/api/auth/getMe.ts'
import useQueryGetAlertBioLive from '@/api/notification/getAlertBioLive.ts'
import useQueryGetMyPermission, { PermissionAction, PermissionSubject } from '@/api/auth/getPermission.ts'
import useQueryGetLiveEmergencyAlert from '@/api/notification/getLiveEmergencyAlert.ts'
import useQueryGetLiveFallDetectionAlert from '@/api/notification/getLiveFallDetectionAlert.ts'
import useQueryGetUsersDeviceStatus from '@/api/user/getUsersDeviceStatus'
import useQueryGetUsersCount from '@/api/user/getUsersCount.ts'
import { getDashboardSummarySource } from '@/pages/dashboard/dashboardSummarySource.ts'
import { useMapLocationStore } from '@/store/MapLocationStore.ts'
import ArrowSelect from '@/assets/ic_arrow_select.svg?react'
import UsersIcon from '@/assets/ic_users.svg?react'
import FallIcon from '@/assets/ic_fall.svg?react'
import PulseIcon from '@/assets/ic_pulse.svg?react'
import { useEmergencyAlertUIStore } from '@/store/EmergencyAlertUIStore.ts'
import TestSessionChip from '@/pages/dashboard/TestSessionChip.tsx'

type IProps = {}

const BOUNDS_DEBOUNCE_MS = 300

const renderMetricIcon = (id: string) => {
    switch (id) {
        case 'total':
            return <UsersIcon aria-hidden />
        case 'emergency':
            return '!'
        case 'fall':
            return <FallIcon aria-hidden />
        case 'bio':
            return <PulseIcon aria-hidden />
        default:
            return '●'
    }
}

const Dashboard: React.FC<IProps> = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { hasPermission } = useQueryGetMyPermission()
    const [bbox, setBbox] = useState<IReqGetUsersBbox>()
    const [isBioAlertListCollapsed, setIsBioAlertListCollapsed] = useState(true)
    const [showBioAlertToast, setShowBioAlertToast] = useState(false)
    const [detailModalId, setDetailModalId] = useState(-1)
    const [bioAlertPanelHeight, setBioAlertPanelHeight] = useState(58)
    const mapRef = useRef<any>()
    const boundsTimerRef = useRef<number>()
    const hasBboxRef = useRef(false)
    const detailModalRef = useRef<any>()
    const bioAlertPanelRef = useRef<HTMLElement | null>(null)
    const bioAlertCountRef = useRef(0)
    const isBioAlertBaselineReadyRef = useRef(false)
    const { consumeTargetLocation } = useMapLocationStore()
    const requestOpenEmergencyAlerts = useEmergencyAlertUIStore(state => state.requestOpen)

    const canReadBioAlerts = hasPermission(PermissionSubject.ALERT_BIO, PermissionAction.READ)
    const { data: bioAlertRes, refetch: refetchBioAlerts } = useQueryGetAlertBioLive(canReadBioAlerts)
    const { data: users } = useQueryGetUsersBbox(bbox)
    const { data: me, isPending: isMePending } = useQueryGetMe()
    const summarySource = getDashboardSummarySource(isMePending, me?.dashboardType)
    const { data: deviceStatus } = useQueryGetUsersDeviceStatus(summarySource.useDeviceStatus)
    const { data: usersCount } = useQueryGetUsersCount(summarySource.useCount)
    const { data: emergencyAlerts } = useQueryGetLiveEmergencyAlert()
    const { data: fallDetectionAlerts } = useQueryGetLiveFallDetectionAlert()

    const isCareManager = useMemo(
        () => me?.dashboardType === DashboardType.CARE_MANAGER,
        [me?.dashboardType]
    )
    const totalUserDeviceCount = useMemo(() => {
        if (isCareManager) {
            if (!deviceStatus) return 0
            return Object.values(deviceStatus).reduce((sum, currentValue) => sum + currentValue, 0)
        }
        return usersCount?.total ?? 0
    }, [deviceStatus, isCareManager, usersCount?.total])
    const markers = useMemo(() => users?.filter(user => user?.location), [users])
    const bioAlerts = bioAlertRes?.bios ?? []
    const emergencyButtonCount = emergencyAlerts?.emergencies?.length || 0
    const fallDetectionCount = fallDetectionAlerts?.falls?.length || 0
    const emergencyCount = emergencyButtonCount + fallDetectionCount
    const hasBioAlerts = canReadBioAlerts && bioAlerts.length > 0

    const dashboardMetrics = useMemo(() => {
        const personUnit = t('account.protectee.count')
        const caseUnit = t('V3.biometricDashboard.stats.cases')
        if (isCareManager) {
            return [
                { id: 'total', label: t('dashboard.metric.total'), value: totalUserDeviceCount, unit: personUnit, tone: 'bg-[#EAF2FF] text-[#205FD8]' },
                { id: 'emergency', label: t('dashboard.metric.emergency'), value: emergencyButtonCount, unit: caseUnit, tone: 'bg-[#FFF0F1] text-[#D9363E]' },
                { id: 'fall', label: t('dashboard.metric.fall'), value: fallDetectionCount, unit: caseUnit, tone: 'bg-[#FFF3E8] text-[#C76818]' },
                { id: 'online', label: t('dashboard.metric.online'), value: deviceStatus?.online || 0, unit: personUnit, tone: 'bg-[#EAF8F3] text-[#13815D]' }
            ]
        }
        return [
            { id: 'total', label: t('dashboard.metric.total'), value: totalUserDeviceCount, unit: personUnit, tone: 'bg-[#EAF2FF] text-[#205FD8]' },
            { id: 'emergency', label: t('dashboard.metric.emergency'), value: emergencyButtonCount, unit: caseUnit, tone: 'bg-[#FFF0F1] text-[#D9363E]' },
            { id: 'fall', label: t('dashboard.metric.fall'), value: fallDetectionCount, unit: caseUnit, tone: 'bg-[#FFF3E8] text-[#C76818]' },
            { id: 'bio', label: t('dashboard.metric.bio'), value: bioAlerts.length, unit: caseUnit, tone: 'bg-[#FFF7E8] text-[#B96B00]' }
        ]
    }, [bioAlerts.length, deviceStatus?.online, emergencyButtonCount, fallDetectionCount, isCareManager, t, totalUserDeviceCount])

    const handleBoundsChange = useCallback((newBbox: IReqGetUsersBbox) => {
        const applyBbox = () => {
            hasBboxRef.current = true
            setBbox(currentBbox => JSON.stringify(currentBbox) === JSON.stringify(newBbox) ? currentBbox : newBbox)
        }

        window.clearTimeout(boundsTimerRef.current)
        if (!hasBboxRef.current) {
            applyBbox()
            return
        }
        boundsTimerRef.current = window.setTimeout(applyBbox, BOUNDS_DEBOUNCE_MS)
    }, [])

    useEffect(() => () => window.clearTimeout(boundsTimerRef.current), [])

    const moveMap = useCallback((latitude?: number, longitude?: number, zoom = 18) => {
        if (
            typeof latitude === 'number' &&
            typeof longitude === 'number' &&
            Number.isFinite(latitude) &&
            Number.isFinite(longitude)
        ) {
            mapRef.current?.setLocation(latitude, longitude, zoom)
        }
    }, [])

    useEffect(() => {
        const targetLocation = consumeTargetLocation()
        if (targetLocation) moveMap(targetLocation.latitude, targetLocation.longitude, 16)
    }, [consumeTargetLocation, moveMap])

    useEffect(() => {
        if (!bioAlertRes) return

        if (!isBioAlertBaselineReadyRef.current) {
            isBioAlertBaselineReadyRef.current = true
            bioAlertCountRef.current = bioAlerts.length
            return
        }

        if (bioAlerts.length > bioAlertCountRef.current) {
            setShowBioAlertToast(true)
            const timer = window.setTimeout(() => setShowBioAlertToast(false), 7000)
            bioAlertCountRef.current = bioAlerts.length
            return () => window.clearTimeout(timer)
        }

        bioAlertCountRef.current = bioAlerts.length
    }, [bioAlertRes, bioAlerts.length])

    useEffect(() => {
        const panel = bioAlertPanelRef.current
        if (!panel || typeof ResizeObserver === 'undefined') return

        const updatePanelHeight = () => {
            setBioAlertPanelHeight(Math.ceil(panel.getBoundingClientRect().height))
        }
        const observer = new ResizeObserver(updatePanelHeight)

        updatePanelHeight()
        observer.observe(panel)

        return () => observer.disconnect()
    }, [isCareManager])

    return (
        <>
            <PageConfig showTopNav showSideNav hideTestModeBanner>
                <div className="care-dashboard relative h-full overflow-hidden bg-[#E9EFF7]">
                    {!isCareManager
                        ? <DashboardMap users={markers} className="h-full w-full" ref={mapRef} onBoundsChange={handleBoundsChange} />
                        : <DashboardMapForCareManager users={markers} className="h-full w-full" ref={mapRef} onBoundsChange={handleBoundsChange} />}

                    {showBioAlertToast && !isCareManager && (
                        <div
                            role="status"
                            className="pointer-events-auto absolute right-[92px] top-[172px] z-[3] flex w-[320px] items-center gap-[11px] rounded-[14px] border border-[#F0D0D2] bg-white p-[13px] shadow-[0_12px_34px_rgba(33,50,78,0.2)] lg:right-[12px] lg:top-[154px] sm:left-[12px] sm:w-auto"
                        >
                            <span className="flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-[11px] bg-[#FFF0F1] text-[#C92C35]"><PulseIcon aria-hidden className="h-[18px] w-[18px]" /></span>
                            <button
                                type="button"
                                className="min-w-0 flex-1 text-left"
                                onClick={() => {
                                    setShowBioAlertToast(false)
                                    setIsBioAlertListCollapsed(false)
                                }}
                            >
                                <strong className="block text-[13px] text-[#26374E]">{t('dashboard.bioAlert.toast.title')}</strong>
                                <span className="mt-[2px] block text-[11px] text-[#748196]">{t('dashboard.bioAlert.toast.desc')}</span>
                            </button>
                            <button
                                type="button"
                                aria-label={t('dashboard.bioAlert.toast.close')}
                                className="flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-full text-[18px] text-[#8290A3] hover:bg-[#F2F5F8]"
                                onClick={() => setShowBioAlertToast(false)}
                            >
                                ×
                            </button>
                        </div>
                    )}

                    <div
                        className={`care-dashboard__overlay ${isBioAlertListCollapsed ? 'care-dashboard__overlay--alerts-collapsed' : ''}`}
                        style={{ '--bio-alert-panel-height': `${bioAlertPanelHeight}px` } as React.CSSProperties}
                    >
                        <section className="care-dashboard__main">
                            <div className="app-scrollbar care-dashboard__metrics">
                                {dashboardMetrics.map(metric => (
                                    <article
                                        key={metric.id}
                                        className="care-dashboard__metric"
                                    >
                                        <div data-tone={metric.id} className={`care-dashboard__metric-icon ${metric.tone}`}>
                                            {renderMetricIcon(metric.id)}
                                        </div>
                                        <p className="care-dashboard__metric-label">{metric.label}</p>
                                        <p className="care-dashboard__metric-value">
                                            {metric.value}<span>{metric.unit}</span>
                                        </p>
                                    </article>
                                ))}
                            </div>

                            <TestSessionChip />

                            <div className="care-dashboard__bottom-actions">
                                {emergencyCount > 0 && !isCareManager && (
                                    <button
                                        type="button"
                                        className="care-dashboard__emergency"
                                        onClick={requestOpenEmergencyAlerts}
                                    >
                                        <span className="flex h-[28px] w-[28px] items-center justify-center rounded-full bg-white/20 text-[16px] font-black">!</span>
                                        <span>
                                            <strong className="block text-[13px]">{t('dashboard.emergencyBanner.title', { count: emergencyCount })}</strong>
                                            <span className="text-[11px] text-white/80">{t('dashboard.emergencyBanner.desc')}</span>
                                        </span>
                                    </button>
                                )}
                                {me?.latitude && me?.longitude && (
                                    <button
                                        type="button"
                                        className="app-subtle-button care-dashboard__locate"
                                        onClick={() => moveMap(me.latitude, me.longitude)}
                                    >
                                        <span aria-hidden>⌖</span>
                                        {t('dashboard.summary.findCenterLocation')}
                                    </button>
                                )}
                            </div>
                        </section>

                        {!isCareManager && (
                            <aside
                                ref={bioAlertPanelRef}
                                className={`care-dashboard__alerts ${isBioAlertListCollapsed ? 'care-dashboard__alerts--collapsed' : 'care-dashboard__alerts--expanded'}`}
                            >
                                {isBioAlertListCollapsed ? (
                                    <>
                                        <button
                                            type="button"
                                            className="care-dashboard__alerts-collapsed-rail min-h-[154px] w-full flex-col items-center gap-[8px] px-[8px] py-[12px] text-center transition hover:bg-[#F7F9FC]"
                                            aria-expanded={false}
                                            aria-label={t('dashboard.bioAlert.openLabel', { count: bioAlerts.length })}
                                            onClick={() => setIsBioAlertListCollapsed(false)}
                                        >
                                            <span className={`flex h-[34px] w-[34px] items-center justify-center rounded-[11px] ${hasBioAlerts ? 'bg-[#FFF0F1] text-[#C92C35]' : 'bg-[#EAF8F3] text-[#188060]'}`}><PulseIcon aria-hidden className="h-[17px] w-[17px]" /></span>
                                            <strong className="text-[17px] text-[#26374E]">{bioAlerts.length}</strong>
                                            <span className="whitespace-pre-line text-[10px] font-bold leading-[1.35] text-[#718097]">{t('dashboard.bioAlert.railLabel')}</span>
                                            <ArrowSelect className="mt-auto h-[13px] w-[13px] -rotate-90" />
                                        </button>
                                        <button
                                            type="button"
                                            className="care-dashboard__alerts-collapsed-bar min-h-[58px] w-full items-center justify-between px-[14px] text-left"
                                            aria-expanded={false}
                                            onClick={() => setIsBioAlertListCollapsed(false)}
                                        >
                                            <span className="flex min-w-0 items-center gap-[9px]">
                                                <span className={`flex h-[32px] w-[32px] shrink-0 items-center justify-center rounded-[10px] ${hasBioAlerts ? 'bg-[#FFF0F1] text-[#C92C35]' : 'bg-[#EAF8F3] text-[#188060]'}`}><PulseIcon aria-hidden className="h-[16px] w-[16px]" /></span>
                                                <span className="truncate text-[13px] font-extrabold text-[#26374E]">{t('dashboard.bioAlert.title')}</span>
                                            </span>
                                            <span className="flex shrink-0 items-center gap-[7px]">
                                                <strong className={hasBioAlerts ? 'text-[#C92C35]' : 'text-[#188060]'}>{t('dashboard.bioAlert.count', { count: bioAlerts.length })}</strong>
                                                <ArrowSelect className="h-[13px] w-[13px] rotate-180" />
                                            </span>
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button
                                            type="button"
                                            className="care-dashboard__alerts-header"
                                            aria-expanded={true}
                                            onClick={() => setIsBioAlertListCollapsed(true)}
                                        >
                                            <span>
                                                <strong className="block text-[15px] text-[#17263C]">{t('dashboard.bioAlert.title')}</strong>
                                                <span className="mt-[2px] block text-[11px] font-medium text-[#7A8799]">{t('dashboard.bioAlert.recent', { count: bioAlerts.length })}</span>
                                            </span>
                                            <span className="flex items-center gap-[8px]">
                                                <span className={`rounded-full px-[8px] py-[3px] text-[11px] font-bold ${hasBioAlerts ? 'bg-[#FFF0F1] text-[#C92C35]' : 'bg-[#EFF6F3] text-[#208264]'}`}>
                                                    {hasBioAlerts ? t('dashboard.bioAlert.needsCheck') : t('dashboard.notification.normal')}
                                                </span>
                                                <ArrowSelect className="h-[14px] w-[14px] -rotate-90 lg:rotate-0" />
                                            </span>
                                        </button>

                                        <div className="app-scrollbar care-dashboard__alerts-list">
                                            {hasBioAlerts ? bioAlerts.map(item => (
                                                <UrgentNotification
                                                    key={item.id}
                                                    className="mb-[8px] w-full last:mb-0"
                                                    {...item}
                                                    onRefreshList={refetchBioAlerts}
                                                    onClickMarker={(latitude, longitude) => {
                                                        moveMap(latitude, longitude)
                                                        setIsBioAlertListCollapsed(true)
                                                    }}
                                                    onClickItem={id => {
                                                        setDetailModalId(id)
                                                        detailModalRef.current?.show()
                                                    }}
                                                />
                                            )) : (
                                                <div className="flex min-h-[180px] flex-col items-center justify-center px-[20px] text-center">
                                                    <span className="mb-[10px] flex h-[40px] w-[40px] items-center justify-center rounded-full bg-[#EAF8F3] text-[18px] text-[#188060]">✓</span>
                                                    <p className="text-[13px] font-bold text-[#28384E]">{t('dashboard.bioAlert.empty.title')}</p>
                                                    <p className="mt-[4px] text-[11px] text-[#8490A1]">{t('dashboard.bioAlert.empty.desc')}</p>
                                                </div>
                                            )}
                                        </div>

                                        <div className="care-dashboard__alerts-footer">
                                            <button type="button" className="app-subtle-button w-full" onClick={() => navigate('/notification?tab=bio')}>
                                                {t('dashboard.bioAlert.viewAll')}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </aside>
                        )}
                    </div>
                </div>
            </PageConfig>
            <BioAlertDetailModal ref={detailModalRef} defaultId={detailModalId} refetchAlerts={refetchBioAlerts} />
        </>
    )
}

export default Dashboard
